// React
import { useCallback, useEffect, useId, useMemo, useState, type KeyboardEvent, type ReactElement } from 'react';

// shadcn/ui
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

// Animation
import { motion, AnimatePresence } from 'framer-motion';

// Shared components
import { CloseIcon } from '@components/icons';

// Hooks
import { useTranslation } from '@hooks/useTranslation';

// Utils
import { cn } from '@/lib/utils';
import { UNDERLINE_SLIDE_IN_GROUP } from '@/components/ui/link-underline';

// Local
import {
  DOCUMENT_KEYS,
  getDocumentAvailability,
  expandDocumentKeys,
  type DocumentKey,
  type SelectedEventDescriptor,
} from './documentAvailability';

interface SelectDocumentsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedEvents: readonly SelectedEventDescriptor[];
  /** Called with the expanded backend document types when the user clicks Generate. */
  onGenerate: (documentTypes: string[]) => void;
}

/** Document groups mirror the two-card and three-card rows in the selection modal design. */
const TOP_ROW_DOCUMENT_KEYS: readonly DocumentKey[] = ['production_and_fulfilment', 'display_copy'];
const BOTTOM_ROW_DOCUMENT_KEYS: readonly DocumentKey[] = ['timestamp', 'dish_label', 'menu_card'];

/** Blur-swap duration for the A↔C shortcut keycap, matching the products FiltersModal. */
const BLUR_SWAP_DURATION_S = 0.15;

/** Per-tile icons, taken verbatim from Figma (node 6961-48109). Stroke/fill use
 *  currentColor so the icon follows the tile's text colour (grey default → off-black
 *  selected → grey-light disabled); inner cut-outs keep the off-white show-through.
 *  Mask IDs come from useId() so multiple mounted modals can't collide on the
 *  document-global SVG id namespace. Production-and-Fulfilment is label-only in the
 *  design, so it has no icon. */
function TimestampIcon() {
  const cardId = useId();
  const stripId = useId();
  return (
    <svg width="10" height="14" viewBox="0 0 10 14" fill="none" aria-hidden="true">
      <mask id={cardId} fill="white">
        <path d="M0 1C0 0.447715 0.447715 0 1 0H9C9.55228 0 10 0.447715 10 1V12H0V1Z" />
      </mask>
      <path
        d="M0 1C0 0.447715 0.447715 0 1 0H9C9.55228 0 10 0.447715 10 1V12H0V1Z"
        stroke="currentColor"
        strokeWidth="2.5"
        mask={`url(#${cardId})`}
      />
      <mask id={stripId} fill="white">
        <path d="M0 10.7998H10V12.7998C10 13.3521 9.55229 13.7998 9 13.7998H1C0.447715 13.7998 0 13.3521 0 12.7998V10.7998Z" />
      </mask>
      <path
        d="M0 10.7998H10V12.7998C10 13.3521 9.55229 13.7998 9 13.7998H1C0.447715 13.7998 0 13.3521 0 12.7998V10.7998Z"
        fill="#FFFCF7"
        stroke="currentColor"
        strokeWidth="2.5"
        mask={`url(#${stripId})`}
      />
    </svg>
  );
}

function DishLabelIcon() {
  const cardId = useId();
  const tabId = useId();
  return (
    <svg width="14" height="12" viewBox="0 0 14 12" fill="none" aria-hidden="true">
      <mask id={cardId} fill="white">
        <rect width="14" height="10" rx="1" />
      </mask>
      <rect width="14" height="10" rx="1" stroke="currentColor" strokeWidth="3" mask={`url(#${cardId})`} />
      <mask id={tabId} fill="white">
        <rect x="4" y="7.5" width="6" height="4" rx="1" />
      </mask>
      <rect
        x="4"
        y="7.5"
        width="6"
        height="4"
        rx="1"
        fill="#FFFCF7"
        stroke="currentColor"
        strokeWidth="3"
        mask={`url(#${tabId})`}
      />
    </svg>
  );
}

function MenuCardIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M3.88369 12.6192H10.1163V2.98576H3.88369V12.6192ZM1.35484 10.6495L2.52886 11.7714V2.65314L1.35484 1.809V10.6495ZM11.4711 2.65314V11.7714L12.6452 10.6495V1.809L11.4711 2.65314ZM14 10.85C13.9999 11.1043 13.8962 11.3476 13.7142 11.5215L11.637 13.5065C11.505 13.6325 11.3533 13.7049 11.1986 13.7366C11.0358 13.899 10.8137 13.9999 10.5679 14H3.43208C3.18604 14 2.96343 13.8992 2.80053 13.7366C2.64614 13.7048 2.49474 13.6323 2.36303 13.5065L0.285786 11.5215C0.103751 11.3476 9.38364e-05 11.1043 0 10.85V0.921716C0.000277939 0.22247 0.72539 -0.204317 1.30897 0.0991543L1.42364 0.170173L3.4215 1.60494H10.5776L12.5764 0.170173C13.1746 -0.259647 13.9997 0.175823 14 0.921716V10.85Z"
        fill="currentColor"
      />
    </svg>
  );
}

const DOCUMENT_ICONS: Partial<Record<DocumentKey, ReactElement>> = {
  timestamp: <TimestampIcon />,
  dish_label: <DishLabelIcon />,
  menu_card: <MenuCardIcon />,
};

export function SelectDocumentsModal({
  open,
  onOpenChange,
  selectedEvents,
  onGenerate,
}: SelectDocumentsModalProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<DocumentKey[]>([]);

  const availability = useMemo(
    () => getDocumentAvailability(selectedEvents),
    [selectedEvents]
  );
  const enabledKeys = useMemo(
    () => DOCUMENT_KEYS.filter((key) => !availability[key].disabled),
    [availability]
  );
  // Never act on a document that became unavailable after it was picked.
  const effectiveSelected = useMemo(
    () => selected.filter((key) => !availability[key].disabled),
    [selected, availability]
  );
  const hasSelection = effectiveSelected.length > 0;

  // Reset the picks each time the modal opens.
  useEffect(() => {
    if (open) setSelected([]);
  }, [open]);

  const toggleDocument = useCallback(
    (key: DocumentKey) => {
      if (availability[key].disabled) return;
      setSelected((prev) =>
        prev.includes(key) ? prev.filter((entry) => entry !== key) : [...prev, key]
      );
    },
    [availability]
  );

  const selectAll = useCallback(() => {
    setSelected([...enabledKeys]);
  }, [enabledKeys]);

  const clearAll = useCallback(() => {
    setSelected([]);
  }, []);

  const handleGenerate = useCallback(() => {
    // Expand the chosen keys into backend document types: Timestamp and Display
    // copy each fan out to both their default and wedding-specific variants,
    // which the backend scope_events routes per event.
    onGenerate(expandDocumentKeys(effectiveSelected));
  }, [onGenerate, effectiveSelected]);

  // Two shortcuts, matching the products FiltersModal: ⌥A / Alt+A selects all,
  // ⌥C / Alt+C clears all. Match on the physical key code OR the character:
  //  - event.code ('KeyA'/'KeyC') is immune to macOS Option composition, which
  //    rewrites event.key to the composed character (Option+A → 'å', Option+C → 'ç');
  //  - event.key ('a'/'å', 'c'/'ç') is the layout-correct character and covers
  //    inputs that don't emit a reliable event.code (assistive tech, virtual keyboards).
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!event.altKey) return;
      const key = event.key?.toLowerCase() ?? '';
      if (event.code === 'KeyA' || key === 'a' || key === 'å') {
        event.preventDefault();
        selectAll();
      } else if (event.code === 'KeyC' || key === 'c' || key === 'ç') {
        event.preventDefault();
        clearAll();
      }
    },
    [selectAll, clearAll]
  );

  const renderTile = (key: DocumentKey) => {
    const isDisabled = availability[key].disabled;
    const isSelected = !isDisabled && effectiveSelected.includes(key);
    const reasonKey = availability[key].reasonKey;

    return (
      <button
        key={key}
        type="button"
        disabled={isDisabled}
        aria-pressed={isDisabled ? undefined : isSelected}
        onClick={() => toggleDocument(key)}
        title={isDisabled && reasonKey ? t(`download_modal.${reasonKey}`) : undefined}
        className={cn(
          'flex flex-col items-center justify-center gap-3 h-doc-card w-full p-8 rounded-input border bg-off-white transition-colors font-inter text-sm leading-[1.5]',
          isDisabled
            ? 'border-grey-light text-grey-light opacity-60 cursor-not-allowed'
            : isSelected
              ? 'border-off-black text-off-black cursor-pointer'
              : 'border-grey-light text-grey hover:border-grey cursor-pointer'
        )}
      >
        {DOCUMENT_ICONS[key]}
        <span>{t(`download_modal.${key}`)}</span>
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onKeyDown={handleKeyDown}
        className="bg-off-white flex flex-col gap-8 p-8 rounded-input sm:rounded-input border-none shadow-none w-full max-w-dialog [&>button]:hidden"
      >
        <div className="flex items-center justify-between">
          <DialogTitle className="font-inter font-semibold text-lg leading-[1.5] text-off-black">
            {t('download_modal.title')}
          </DialogTitle>
          <DialogClose
            aria-label={t('download_modal.close')}
            className="text-grey hover:text-off-black transition-colors focus:outline-none"
          >
            <CloseIcon className="h-3.5 w-3.5" />
          </DialogClose>
        </div>
        <DialogDescription className="sr-only">{t('download_modal.description')}</DialogDescription>

        <div className="border-t border-dinnerplate-white -mx-8" />

        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            {TOP_ROW_DOCUMENT_KEYS.map((key) => renderTile(key))}
          </div>
          <div className="grid grid-cols-3 gap-4">
            {BOTTOM_ROW_DOCUMENT_KEYS.map((key) => renderTile(key))}
          </div>
        </div>

        <div className="border-t border-dinnerplate-white -mx-8" />

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={hasSelection ? clearAll : selectAll}
            className="group/underline flex items-center gap-3 focus:outline-none"
          >
            <span className="flex items-center gap-1.5" aria-hidden="true">
              <span className="flex h-6 w-6 items-center justify-center rounded-xs border border-dinnerplate-white text-grey font-inter text-xs leading-[1.25]">
                ⌥
              </span>
              <span className="flex h-6 w-6 items-center justify-center rounded-xs border border-dinnerplate-white text-grey font-inter text-xs leading-[1.25]">
                <AnimatePresence mode="popLayout" initial={false}>
                  <motion.span
                    key={hasSelection ? 'C' : 'A'}
                    initial={{ opacity: 0, filter: 'blur(4px)' }}
                    animate={{ opacity: 1, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, filter: 'blur(4px)' }}
                    transition={{ duration: BLUR_SWAP_DURATION_S, ease: 'easeOut' }}
                  >
                    {hasSelection ? 'C' : 'A'}
                  </motion.span>
                </AnimatePresence>
              </span>
            </span>
            <span className={cn(UNDERLINE_SLIDE_IN_GROUP, 'font-inter text-sm font-semibold text-grey group-hover/underline:text-off-black')}>
              {hasSelection ? t('download_modal.clear_all') : t('download_modal.select_all')}
            </span>
          </button>

          <Button
            onClick={handleGenerate}
            disabled={effectiveSelected.length === 0}
          >
            {t('download_modal.generate')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
